/**
 * Minimal history-API client-side router. Framework-free.
 * - Intercepts same-origin link clicks inside the app root.
 * - Matches paths to route keys (locale inferred from the localized path).
 * - Exposes `navigate(key, params, query)` for programmatic navigation and the
 *   FrontendApp contract.
 */
import type { Locale } from "@mb-group/shared-types";
import {
	buildHref,
	matchLocation,
	pathTemplate,
	type AppRouteKey,
	type QueryParams,
	type RouteMatch,
	type RouteParams,
} from "./routes.js";

export interface RouterHost {
	getLocale(): Locale;
	/** A matched path implied a different locale (or the user switched language). */
	onLocaleInferred(locale: Locale): void;
	/** Render for a match, or null → 404. */
	render(match: RouteMatch | null): void | Promise<void>;
}

/** Where the page rendered for one match lives in other languages (see setAlternates). */
interface Alternates {
	match: RouteMatch;
	params: Partial<Record<Locale, RouteParams | null>>;
	fallback: AppRouteKey;
}

export class Router {
	private started = false;
	private current: RouteMatch | null = null;
	private alternates: Alternates | null = null;
	private readonly onClick = (e: MouseEvent) => this.handleClick(e);
	private readonly onPop = () => {
		void this.handle();
	};

	constructor(
		private readonly root: HTMLElement,
		private readonly host: RouterHost,
	) {}

	start(): void {
		if (this.started) return;
		this.started = true;
		this.root.addEventListener("click", this.onClick);
		window.addEventListener("popstate", this.onPop);
		void this.handle();
	}

	stop(): void {
		if (!this.started) return;
		this.started = false;
		this.root.removeEventListener("click", this.onClick);
		window.removeEventListener("popstate", this.onPop);
	}

	getCurrent(): RouteMatch | null {
		return this.current;
	}

	/**
	 * Declare where the page rendered for `match` lives in other languages, for
	 * a route whose params are translated (a case study has its own slug per
	 * language). Per locale: the params that differ there, or null when the page
	 * has no version in that language; the language switch then goes to
	 * `fallback` there and no hreflang is emitted for it. Bound to `match`: it
	 * lapses with the next navigation, and a slow view cannot attach it to the
	 * page that replaced it.
	 */
	setAlternates(
		match: RouteMatch | null,
		params: Partial<Record<Locale, RouteParams | null>>,
		fallback: AppRouteKey,
	): void {
		if (match) this.alternates = { match, params, fallback };
	}

	/**
	 * The current page in `locale`: its own params unless a view declared
	 * translated ones (setAlternates); null when there is no current page or it
	 * has no version in that language.
	 */
	hrefIn(locale: Locale): string | null {
		const match = this.current;
		if (!match) return null;
		const declared = this.alternates?.match === match ? this.alternates.params[locale] : undefined;
		if (declared === null) return null;
		return buildHref(match.key, locale, { ...match.params, ...declared }, match.query);
	}

	/** Programmatic navigation by route key (also the FrontendApp.navigate impl). */
	navigate(key: AppRouteKey, params?: RouteParams, query?: QueryParams): void {
		const href = buildHref(key, this.host.getLocale(), params, query);
		this.go(href);
	}

	/** Navigate to a raw internal path (pathname + optional search). */
	go(path: string): void {
		const url = new URL(path, window.location.origin);
		if (url.pathname + url.search === window.location.pathname + window.location.search) {
			// Same location: still re-render (e.g. filter re-application handled by views reading query).
			void this.handle();
			return;
		}
		window.history.pushState({}, "", url.pathname + url.search);
		void this.handle();
	}

	/**
	 * Switch UI language: re-navigate the current route under the target locale's
	 * localized path so the URL, chrome and content all follow.
	 */
	switchLocale(target: Locale): void {
		const match = this.current;
		if (!match) {
			this.host.onLocaleInferred(target);
			this.go(target === "pl" ? "/" : "/");
			return;
		}
		// Root is shared; just flip the locale and re-render.
		if (pathTemplate(match.key, "pl") === pathTemplate(match.key, "en")) {
			this.host.onLocaleInferred(target);
			const href = buildHref(match.key, target, match.params, match.query);
			window.history.replaceState({}, "", href);
			this.current = { ...match, locale: target };
			void this.host.render(this.current);
			return;
		}
		// A case study's slug is translated: the view said where it lives (or
		// that it does not exist) in the other language.
		const fallback = this.alternates?.match === match ? this.alternates.fallback : match.key;
		this.host.onLocaleInferred(target);
		this.go(this.hrefIn(target) ?? buildHref(fallback, target));
	}

	private async handle(): Promise<void> {
		const match = matchLocation(
			window.location.pathname,
			window.location.search,
			this.host.getLocale(),
		);
		if (match && match.locale !== this.host.getLocale()) {
			this.host.onLocaleInferred(match.locale);
		}
		this.current = match;
		await this.host.render(match);
	}

	private handleClick(e: MouseEvent): void {
		if (e.defaultPrevented || e.button !== 0) return;
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		const target = e.target as Element | null;
		const anchor = target?.closest("a");
		if (!anchor) return;
		const href = anchor.getAttribute("href");
		if (!href || href.startsWith("#")) return;
		if (
			anchor.hasAttribute("data-native") ||
			anchor.hasAttribute("download") ||
			anchor.getAttribute("target") === "_blank" ||
			anchor.getAttribute("rel")?.includes("external")
		) {
			return;
		}
		const url = new URL(href, window.location.href);
		if (url.origin !== window.location.origin) return;
		e.preventDefault();
		this.go(url.pathname + url.search);
	}
}
